import { LocationSearchResponse } from 'auth/auth.types';
import { API_URL } from 'shared/constants';
import {
  getCurrentLocationId,
  processLocationValidationStatus,
} from 'shared/helpers/utils';

import { CustomAxios } from '../axios-helpers';

export interface SearchLocationData {
  active: boolean;
  locationID: string;
  orgId: string;
  name: string;
  parent_id: string | null;
  phone_tenant_id: string | null;
  slug: string;
  opsType: string;
  email: string;
  timezone: string;
  salesforceId: string;
  modifiedAt: string | null;
  createdAt: string | null;
  vertical: string;
  deletedAt: string | null;
  canceledAt: string | null;
  lastSyncedAt: string | null;
}

export async function searchLocation(searchTerm: string): Promise<SearchLocationData[]> {
  if (!searchTerm) {
    return [];
  }

  const LOCATION_SEARCH_URL = '/location/v2/data-location/locations/search';
  const { data } = await CustomAxios.get<LocationSearchResponse>(LOCATION_SEARCH_URL, {
    baseURL: API_URL,
    params: { searchText: searchTerm },
  });

  const locations = Array.isArray(data?.locations) ? data.locations : [];

  return locations.map((location) => ({
    active: location.active.toLowerCase() === 'true',
    locationID: location.locationId,
    orgId: location.orgId,
    name: location.name,
    parent_id: location.parentId ?? null,
    phone_tenant_id: location.phoneTenantId ?? null,
    slug: location.slug,
    opsType: location.opsType,
    email: location.email,
    timezone: location.timezone,
    salesforceId: location.salesforceId,
    modifiedAt: location.modifiedAt,
    createdAt: location.createdAt,
    vertical: location.vertical,
    deletedAt: location.deletedAt,
    canceledAt: location.canceledAt,
    lastSyncedAt: location.lastSyncedAt,
  }));
}

export async function validateLocation(locationId = getCurrentLocationId()) {
  try {
    const response = await CustomAxios.post('/onboard-company', {
      company_id: locationId,
    });
    return response.status;
  } catch (e: any) {
    return e.status as number;
  }
}

export async function validateLocations(locations: string[]) {
  const promises: Promise<boolean>[] = [];

  for (const location of locations) {
    const promise = validateLocation(location).then(
      (status) => processLocationValidationStatus(status).isValidLocation
    );

    promises.push(promise);
  }

  const result = await Promise.all(promises);
  const hasInvalidLocation = result.some((value) => value === false);
  const invalidLocations: string[] = [];

  if (hasInvalidLocation) {
    for (const index in result) {
      if (!result[index]) {
        invalidLocations.push(locations[index]);
      }
    }
  }

  return {
    result,
    hasInvalidLocation,
    invalidLocations,
  };
}
